import React, { useState, useRef, useEffect, useCallback } from 'react';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const containerRef        = useRef<HTMLDivElement>(null);
  const inputRef            = useRef<HTMLInputElement>(null);

  const selected = options.find(o => String(o.value) === String(value));

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = useCallback((opt: SelectOption) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }} className={className}>
      {/* Trigger */}
      <div
        className={`form-control form-control-solid d-flex align-items-center justify-content-between${disabled ? ' disabled' : ''}`}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          minHeight: 42,
          paddingRight: 36,
          opacity: disabled ? 0.65 : 1,
        }}
        onClick={handleOpen}
      >
        <span className={selected ? 'text-dark' : 'text-muted'} style={{ fontSize: 14 }}>
          {selected ? selected.label : placeholder}
        </span>
        <span
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: selected ? 'auto' : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {selected && (
            <span
              onClick={handleClear}
              style={{ cursor: 'pointer', color: '#a1a5b7', fontSize: 14, lineHeight: 1 }}
              title="Clear"
            >
              ×
            </span>
          )}
          <svg
            width="12" height="12"
            viewBox="0 0 24 24"
            fill="none"
            style={{
              transition: 'transform 0.15s',
              transform: open ? 'rotate(180deg)' : 'none',
              color: '#a1a5b7',
              marginLeft: 2,
            }}
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1050,
            background: '#fff',
            border: '1px solid #e4e6ef',
            borderRadius: 8,
            boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
            overflow: 'hidden',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f2' }}>
            <div style={{ position: 'relative' }}>
              <svg
                width="14" height="14"
                viewBox="0 0 24 24"
                fill="none"
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#a1a5b7' }}
              >
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2.5" />
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="form-control form-control-solid form-control-sm"
                style={{ paddingLeft: 32, fontSize: 13 }}
                placeholder="Search…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setOpen(false); setQuery(''); }
                  if (e.key === 'Enter' && filtered.length === 1) handleSelect(filtered[0]);
                }}
              />
            </div>
          </div>

          {/* Options list */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 14px', color: '#a1a5b7', fontSize: 13 }}>No results</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: '9px 14px',
                    fontSize: 14,
                    cursor: 'pointer',
                    background: String(opt.value) === String(value) ? 'rgba(57,106,84,0.08)' : 'transparent',
                    color: String(opt.value) === String(value) ? '#396A54' : '#181c32',
                    fontWeight: String(opt.value) === String(value) ? 600 : 400,
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => {
                    if (String(opt.value) !== String(value))
                      (e.currentTarget as HTMLDivElement).style.background = '#f9fafb';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.background =
                      String(opt.value) === String(value) ? 'rgba(57,106,84,0.08)' : 'transparent';
                  }}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
